import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useAppContext } from '../../contexts/AppContext';
import { useFormContext } from '../../contexts/FormContext';
import { useFormData } from '../../hooks/useFormValues';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Backdrop, Box, CircularProgress, Stack } from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { plotLines, textLabels } from '../../constants';
import { ExportCSV, formatRainValue, getYearlyDays } from '../../utils/utils';
import { useQuery } from '@tanstack/react-query';
import { Server } from '../../services/Server';
import { handleError } from '../../utils/Error';
import UploadField from '../inputs/UploadField';
import type { FieldSchema } from '../form-engine/types';

dayjs.extend(dayOfYear);

type CurveData = {
  name: string;
  times: string[];
  values: (number | null)[];
  file_uid?: string;
  curve_rain_threshold?: number;
  curve_rain_efficiency?: number;
  rain?: (number | null)[];
};

type ChartValues = Record<string, (number | null)[]>;

// Rain thresholds the curves were calculated with, shown as lines on the rain axis.
// The threshold is only drawn when all curves share one value; with several thresholds no line is shown.
const uniqueRainThresholds = (curves: CurveData[]): number[] => {
  const thresholds = [...new Set(curves.map((curve) => curve.curve_rain_threshold).filter((t): t is number => t != null))];
  return thresholds.length === 1 ? thresholds : [];
};

type ChartPayload = {
  dates?: string[];
  times?: string[];
  values?: ChartValues | (number | null)[];
  rain_dates?: string[];
  cleaning_dates?: number[];
};

type ChartProperties = {
  type?: 'line' | 'column';
  label?: string;
  ylabel?: string;
  ymin?: number;
  ymax?: number;
  show_rain_threshold?: boolean; // Draws the calculation's rain threshold on the rain axis, when all curves share one value.
};

type UploadConfig = FieldSchema & { target?: string };

type Props = {
  getDataFromApi: boolean;
  data: ChartPayload | CurveData[] | undefined;
  url: string;
  method: string;
  params: Record<string, unknown> | undefined;
  body: object;
  properties: Record<string, unknown> | undefined;
  upload?: unknown;
  title?: string;
  data_type?: string;
};

const EMPTY_DATA: ChartPayload = {};

const SMTChart = ({ getDataFromApi, data, url, method, params, body, properties: rawProperties, upload, title, data_type }: Props) => {
  const properties = rawProperties as ChartProperties | undefined;
  const dataPayload = Array.isArray(data) ? undefined : data;
  const { isDarkMode } = useAppContext();
  const { getFormDataItem } = useFormContext();
  const formData = useFormData();
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});
  const xAxisCategories = Array.isArray(chartOptions.xAxis) ? undefined : chartOptions.xAxis?.categories;
  const firstSeriesData = (chartOptions.series?.[0] as Highcharts.SeriesLineOptions | undefined)?.data;
  const chartComponent = useRef(null);
  const { URL_BASE, getFromServerWithParams, postToServerWithParams } = Server();

  // Parse date-only strings (YYYY-MM-DD) as UTC midnight to avoid DST/local timezone shifts
  const parseDateToUtcMs = (dateInput: string): number => {
    if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [y, m, d] = dateInput.split('-').map((v: string) => Number(v));
      return Date.UTC(y, m - 1, d);
    }
    return new Date(dateInput).getTime();
  };

  // Function to determine if the query should be enabled
  const shouldEnableQuery = () => {
    if (url !== 'tools/yield-evaluation') {
      // If no URL, do not enable
      if (!url) {
        return false;
      }

      // For soiling-ratios everything (sensor files + context) is read from the persisted project,
      // so fetch as soon as we have a project id — do not gate on the unreliable from_api flag.
      if (url === 'soiling-ratios') {
        return !!params?.project_id;
      }

      return !!getDataFromApi;
    }

    const source = formData?.yield_evaluation?.source;

    if (source === 'api') {
      // Option 1: Via API - always enable
      return true;
    }

    if (source === 'monthly') {
      // Option 3: Manual monthly values - enable if we have values
      const monthlyValues = formData?.yield_evaluation?.monthly_values;
      return monthlyValues && monthlyValues.length === 12;
    }

    if (source === 'csv') {
      // Option 2: Via CSV upload - enable only if the pattern is validated
      const pattern = formData?.yield_evaluation?.pattern;
      // The pattern is validated if it exists and is not a string (file path)
      return pattern && typeof pattern !== 'string';
    }

    return false;
  };

  // /soiling-ratios reads the uploaded sensor files and the context (ex_post) from the persisted
  // project, so the query must refetch when either changes. Without this the chart keeps stale data
  // after an upload and only refreshes on remount (e.g. switching the ex-post/operational tab), and
  // both tabs would otherwise share the same cache entry.
  const soilingRatiosCacheKey = useMemo(() => {
    if (url !== 'soiling-ratios') {
      return null;
    }
    const sensorFiles = Object.keys(formData)
      .filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'))
      .map((blockKey) => ({
        uid: getFormDataItem(formData, `${blockKey}.file_uid`),
        type: getFormDataItem(formData, `${blockKey}.type`),
      }))
      .filter((file) => file.uid);
    return JSON.stringify({ ex_post: params?.ex_post, project_id: params?.project_id, sensorFiles });
  }, [url, formData, getFormDataItem, params?.ex_post, params?.project_id]);

  const {
    data: dataFromApi = EMPTY_DATA,
    isLoading: isLoading,
    isFetching: isFetching,
    isError: isError,
    error: error,
  } = useQuery<ChartPayload | CurveData[]>({
    queryKey: ['smt-chart-', url, soilingRatiosCacheKey],
    queryFn: () =>
      method === 'get'
        ? getFromServerWithParams(`${URL_BASE}${url}`, params ?? {})
        : postToServerWithParams(`${URL_BASE}${url}`, params ?? {}, body),
    enabled: shouldEnableQuery(),
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 0, // Consider data as stale immediately
  });

  const apiPayload = Array.isArray(dataFromApi) ? undefined : dataFromApi;

  useEffect(() => {
    // Single source of truth to avoid the two effects (dataFromApi vs data) competing and blanking
    // the chart depending on render order. When the chart is API-driven (getDataFromApi) or is the
    // yield-evaluation chart, the fetched data is authoritative; otherwise the persisted `data` prop
    // is preferred, falling back to the API data.
    const source = getDataFromApi || url === 'tools/yield-evaluation' ? dataFromApi : data || dataFromApi;

    if (Array.isArray(source)) {
      // New format: array of curves
      prepareHighcharts(prepareData(source), undefined, properties?.show_rain_threshold ? uniqueRainThresholds(source) : []);
    } else if (source && Object.prototype.hasOwnProperty.call(source, 'dates')) {
      prepareHighcharts(prepareData(source), source.dates);
    } else if (source && Object.prototype.hasOwnProperty.call(source, 'times')) {
      // Special handling for yield-evaluation and soiling sensor data endpoints
      prepareHighcharts(prepareData(source), source.times);
    }
  }, [dataFromApi, data, getDataFromApi, url]);

  useEffect(() => {
    if (isError) {
      handleError(error);
    }
  }, [isError, error]);

  const uploadConfig = upload as UploadConfig | undefined;

  // Function to check if upload field should be hidden
  const isUploadHidden = () => {
    if (!uploadConfig || !Object.prototype.hasOwnProperty.call(uploadConfig, 'isHidden')) {
      return false;
    }

    const isHiddenValue = getFormDataItem(formData, uploadConfig.isHidden!);

    // For yield_evaluation.source, hide upload when source is 'monthly'
    if (uploadConfig.isHidden === 'yield_evaluation.source') {
      const source = getFormDataItem(formData, 'yield_evaluation.source');
      return source === 'monthly';
    }

    return isHiddenValue;
  };

  // Function to get sensor name from file_uid
  const getSensorName = (fileUid: string) => {
    if (!fileUid) return '';

    // Find all soiling sensor data blocks
    const soilingDataKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));

    for (const blockKey of soilingDataKeys) {
      const blockFileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      if (blockFileUid === fileUid) {
        const sensorName = getFormDataItem(formData, `${blockKey}.name`);
        return sensorName || `Sensor ${fileUid.substring(0, 8)}...`;
      }
    }

    return `Sensor ${fileUid.substring(0, 8)}...`;
  };

  const prepareData = (data: ChartPayload | CurveData[]) => {
    const timeseries_data: Highcharts.SeriesOptionsType[] = [];

    // Check if data is in the new array format (multiple curves)
    if (Array.isArray(data)) {
      // New format: array of objects with name, times, values, file_uid
      data.forEach((curve, index) => {
        if (curve.name && curve.times && curve.values) {
          // Convert data to datetime format for proper spacing
          const datetimeData = curve.values
            .map((value, valueIndex): [number, number | null] | null => {
              const date = curve.times[valueIndex];
              if (!date) return null; // Skip if date is missing
              const ts = parseDateToUtcMs(date);
              return [ts, value === null || value === undefined ? null : value];
            })
            .filter((item) => item !== null); // Only remove entries where date is missing

          // Generate colors for different curves
          const colors = ['#FF6B35', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'];
          const color = colors[index % colors.length];

          // Get sensor name and format the curve name
          const sensorName = curve.file_uid ? getSensorName(curve.file_uid) : '';
          const baseName = sensorName ? `${sensorName} (${curve.name})` : curve.name;
          // Each curve is computed with its own rain parameters, shown in the legend.
          const { curve_rain_threshold: threshold, curve_rain_efficiency: efficiency } = curve;
          const curveName =
            threshold != null && efficiency != null
              ? `${baseName} (threshold=${formatRainValue(threshold)}, efficiency=${formatRainValue(efficiency)})`
              : baseName;

          timeseries_data.push({
            name: curveName,
            data: datetimeData,
            color: color,
            type: properties?.type || 'line',
            zIndex: index + 1,
            connectNulls: false, // Don't connect points when there are missing values
          });
        }
      });

      // Rain used by the calculation, merged by date since the curves can cover different periods.
      const rainByTime = new Map<number, number>();
      data.forEach((curve) => {
        curve.rain?.forEach((value, valueIndex) => {
          const date = curve.times[valueIndex];
          if (!date || value == null) return;
          rainByTime.set(parseDateToUtcMs(date), value);
        });
      });
      if (rainByTime.size > 0) {
        timeseries_data.push({
          name: plotLines.rain.name,
          type: 'column',
          color: plotLines.rain.color,
          yAxis: 1,
          zIndex: 0,
          data: Array.from(rainByTime.entries()).sort(([a], [b]) => a - b),
        });
      }
      return timeseries_data;
    }

    // Special handling for yield-evaluation endpoint
    if (url === 'tools/yield-evaluation') {
      const values = data.values as ChartValues;
      // Add dc_no_soiling first so it renders at the back
      if (Object.prototype.hasOwnProperty.call(values, 'dc_no_soiling')) {
        timeseries_data.push({
          name: textLabels.dc_no_soiling,
          data: values.dc_no_soiling,
          color: plotLines['dc_no_soiling'].color,
          type: properties?.type ?? 'line',
          zIndex: 1,
          dashStyle: 'Dash',
        });
      }

      if (Object.prototype.hasOwnProperty.call(values, 'ac_no_soiling')) {
        timeseries_data.push({
          name: textLabels.ac_no_soiling,
          data: values.ac_no_soiling,
          color: plotLines['ac_no_soiling'].color,
          type: properties?.type ?? 'line',
          zIndex: 2,
        });
      }

      if (Object.prototype.hasOwnProperty.call(values, 'ac_no_soiling_clipping')) {
        timeseries_data.push({
          name: textLabels.ac_no_soiling_clipping,
          data: values.ac_no_soiling_clipping,
          color: plotLines['ac_no_soiling_clipping'].color,
          type: properties?.type ?? 'line',
          zIndex: 2,
        });
      }

      if (Object.prototype.hasOwnProperty.call(values, 'ac_soiling_no_clipping')) {
        timeseries_data.push({
          name: textLabels.ac_soiling_no_clipping,
          data: values.ac_soiling_no_clipping,
          color: plotLines['ac_soiling_no_clipping'].color,
          type: properties?.type ?? 'line',
          zIndex: 3,
          dashStyle: 'Dash',
        });
      }

      if (Object.prototype.hasOwnProperty.call(values, 'ac_soiling')) {
        timeseries_data.push({
          name: textLabels.ac_soiling,
          data: values.ac_soiling,
          color: plotLines['ac_soiling'].color,
          type: properties?.type ?? 'line',
          zIndex: 4,
        });
      }

      if (Object.prototype.hasOwnProperty.call(values, 'ac_soiling_clipping')) {
        timeseries_data.push({
          name: textLabels.ac_soiling_clipping,
          data: values.ac_soiling_clipping,
          color: plotLines['ac_soiling_clipping'].color,
          type: properties?.type ?? 'line',
          zIndex: 4,
        });
      }
    } else if (!url && data_type === 'soiling_sensor_data') {
      // Special handling for soiling sensor data endpoint
      // Process all available data keys uniformly
      const chartValues = data.values as ChartValues;

      const processDataKey = (key: string, index: number) => {
        const datetimeData: [number, number | null][] = [];
        const times = data.times || [];
        const values = chartValues[key] || [];

        const timesMs = times.map((t) => parseDateToUtcMs(t));
        const startTime = timesMs.length > 0 ? timesMs[0] : null;
        const endTime = timesMs.length > 0 ? timesMs[timesMs.length - 1] : null;

        if (startTime !== null && endTime !== null) {
          for (let t = startTime; t <= endTime; t += 24 * 60 * 60 * 1000) {
            const dataIndex = timesMs.indexOf(t);
            if (dataIndex !== -1) {
              const value = values[dataIndex];
              datetimeData.push([t, value === null || value === undefined ? null : value]);
            } else {
              datetimeData.push([t, null]);
            }
          }
        } else {
          const fallbackData = values
            .map((value, index): [number, number | null] | null => {
              const ts = parseDateToUtcMs(times[index]);
              if (isNaN(ts)) return null;
              return [ts, value === null || value === undefined ? null : value];
            })
            .filter((item) => item !== null);
          datetimeData.push(...fallbackData);
        }

        // Define colors for different series
        const colors = ['#FF6B35', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
        const color = colors[index % colors.length];

        const sensorName = 'Sensor'; // Default fallback
        timeseries_data.push({
          name: `${sensorName} (${key})`,
          data: datetimeData,
          color: color,
          type: properties?.type || 'line',
          zIndex: index + 1,
          connectNulls: false, // Don't connect points when there are missing values
        });
      };

      // Process all data keys that exist in the values object
      const dataKeys = Object.keys(chartValues || {});
      dataKeys.forEach((key, index) => {
        processDataKey(key, index);
      });
    } else {
      const key = 'value';
      timeseries_data.push({
        name: properties?.label,
        data: data?.values as (number | null)[] | undefined,
        color: plotLines[key].color,
        type: properties?.type ?? 'line',
      });
    }

    return timeseries_data;
  };

  const prepareHighcharts = (
    highChartsData: Highcharts.SeriesOptionsType[],
    dates: string[] | undefined,
    rainThresholds: number[] = [],
  ) => {
    const chart: Highcharts.Options = {
      chart: {
        type: 'line',
        zooming: { type: 'x' },
      },
      title: {
        text: title,
        align: 'left',
      },
      xAxis: {
        ...((!url && data_type === 'soiling_sensor_data') || Array.isArray(dataFromApi) || Array.isArray(data)
          ? {
              // For soiling sensor data or new array format, use datetime axis to handle irregular date intervals
              type: 'datetime',
              dateTimeLabelFormats: {
                day: '%Y-%m-%d',
                week: '%Y-%m-%d',
                month: '%Y-%m',
                year: '%Y',
              },
              title: { text: 'Date' },
              gridLineWidth: 1,
            }
          : {
              // For other charts, use categories as before
              categories: dates?.map((x) => {
                const d = new Date(Date.parse(x));
                // Special formatting for yield-evaluation to include hours and minutes
                if (url === 'tools/yield-evaluation') {
                  return (
                    d.getFullYear() +
                    '-' +
                    ('0' + (d.getMonth() + 1)).slice(-2) +
                    '-' +
                    ('0' + d.getDate()).slice(-2) +
                    ' ' +
                    ('0' + d.getHours()).slice(-2) +
                    ':' +
                    ('0' + d.getMinutes()).slice(-2)
                  );
                } else {
                  return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
                }
              }),
              title: { text: 'Date' },
              gridLineWidth: 1,
            }),
        plotLines: [
          ...(dataPayload && Object.prototype.hasOwnProperty.call(dataPayload, 'rain_dates')
            ? getYearlyDays(dataPayload?.rain_dates ?? []).map((val) => {
                return {
                  label: {
                    text: 'Rain',
                    style: {
                      fontSize: '10px',
                    },
                  },
                  color: 'rgba(0, 0, 255, 0.7)',
                  value:
                    (!url && data_type === 'soiling_sensor_data') || Array.isArray(dataFromApi) || Array.isArray(data)
                      ? new Date(val).getTime()
                      : dayjs(val).dayOfYear() - 1,
                  width: 2,
                };
              })
            : []),
          ...(dataPayload && Object.prototype.hasOwnProperty.call(dataPayload, 'cleaning_dates')
            ? getYearlyDays(dataPayload?.cleaning_dates ?? []).map((val) => {
                return {
                  label: {
                    text: 'Cleaning',
                    style: {
                      fontSize: '10px',
                    },
                  },
                  color: 'rgba(255, 165, 0, 1)',
                  value:
                    (!url && data_type === 'soiling_sensor_data') || Array.isArray(dataFromApi) || Array.isArray(data)
                      ? new Date(val).getTime()
                      : dayjs(val).dayOfYear() - 1,
                  width: 2,
                };
              })
            : []),
        ],
      },
      yAxis: [
        {
          title: { text: properties?.ylabel },
          min: properties?.ymin ?? undefined,
          max: properties?.ymax ?? undefined,
          // Force strict limits if ymin and ymax are defined
          ...(properties?.ymin !== undefined && properties?.ymax !== undefined
            ? {
                tickInterval: 0.2, // Tick marks every 0.2
                startOnTick: true, // Start on a tick mark
                endOnTick: true, // End on a tick mark
                allowDecimals: true,
              }
            : {}),
        },
        ...(highChartsData.some((series) => series.yAxis === 1) || rainThresholds.length > 0
          ? [
              {
                title: { text: plotLines.rain.label },
                opposite: true,
                min: 0,
                plotLines: rainThresholds.map((threshold) => ({
                  value: threshold,
                  color: plotLines.rain_threshold.color,
                  width: 2,
                  dashStyle: 'Dash' as const,
                  zIndex: 5,
                  label: {
                    text: `${plotLines.rain_threshold.label} (${formatRainValue(threshold)} mm/day)`,
                    align: 'right' as const,
                    style: { fontSize: '10px' },
                  },
                })),
              },
            ]
          : []),
      ],
      legend: {
        enabled: true,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        valueDecimals: 4,
        shared: true,
        // Show day-level dates for soiling sensor data that now comes without hours
        ...(!url && data_type === 'soiling_sensor_data'
          ? {
              xDateFormat: '%Y-%m-%d',
            }
          : {}),
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadSVG',
              'separator',
              'downloadCSV',
            ],
          },
        },
        csv: {
          itemDelimiter: ';',
          decimalPoint: ',',
          dateFormat: '%Y-%m-%d',
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  // Helper function to get the sensor type from upload field target
  const getSensorType = () => {
    if (!uploadConfig?.target) return 'rde300';

    // Extract the block name from the upload target (e.g., "soiling_sensor_data.pattern" -> "soiling_sensor_data")
    const blockName = uploadConfig.target.split('.')[0];

    // Get the sensor type from the form data
    const sensorType = getFormDataItem<string | undefined>(formData, `${blockName}.type`);

    return sensorType || 'rde300';
  };

  return (
    <>
      <Box sx={{ pl: 1, pr: 1 }}>
        {isLoading || isFetching ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : (
          [
            <Stack key="chart-controls" direction="row" spacing={2} mb={2} ml={0}>
              {uploadConfig && !isUploadHidden() ? <UploadField field={uploadConfig} /> : null}
              {uploadConfig && !isUploadHidden() && !url && data_type === 'soiling_sensor_data' ? (
                <ExportCSV
                  header={['Date', 'values']}
                  data={{
                    dates: xAxisCategories,
                    rawDates: dataPayload?.times || apiPayload?.times,
                  }}
                  fileName={title?.replace(/[^a-zA-Z0-9]/g, '_')}
                  dateFormat="YYYY-MM-DD"
                  unit={properties?.ylabel}
                  isSoilingSensorData={true}
                  sensorType={getSensorType()}
                />
              ) : null}
              {uploadConfig && !isUploadHidden() && url ? (
                <ExportCSV
                  header={['Date', 'values']}
                  data={(() => {
                    switch (url) {
                      case 'tools/yield-evaluation':
                        return {
                          dates: xAxisCategories,
                          rawDates: apiPayload?.times || dataPayload?.times,
                          values: firstSeriesData,
                        };

                      case 'soiling-ratios':
                        if (Array.isArray(dataFromApi)) {
                          const targetCurve = dataFromApi.find((curve) => curve.name === 'HSU') || dataFromApi[0];
                          return {
                            dates: xAxisCategories,
                            rawDates: targetCurve?.times,
                            values: targetCurve?.values,
                          };
                        }
                        return {
                          dates: xAxisCategories,
                          rawDates: undefined,
                          values: firstSeriesData,
                        };

                      default: {
                        // Handle the case where chartOptions?.series?.[0].data is an array of [timestamp, value] pairs
                        const seriesData = firstSeriesData as [number, number | null][] | undefined;
                        if (
                          Array.isArray(seriesData) &&
                          seriesData.length > 0 &&
                          Array.isArray(seriesData[0]) &&
                          seriesData[0].length >= 2
                        ) {
                          return {
                            dates:
                              xAxisCategories ||
                              seriesData.map((point: [number, number | null]) => {
                                return new Date(point[0]).toISOString().split('T')[0];
                              }),
                            rawDates: undefined,
                            values: seriesData.map((point: [number, number | null]) => point[1]),
                          };
                        }
                        return {
                          dates: xAxisCategories,
                          rawDates: undefined,
                          values: seriesData,
                        };
                      }
                    }
                  })()}
                  fileName={title?.replace(/[^a-zA-Z0-9]/g, '_')}
                  dateFormat={url === 'tools/yield-evaluation' ? 'YYYY-MM-DD HH:mm' : url === 'soiling-ratios' ? 'YYYY-MM-DD' : undefined}
                  unit={properties?.ylabel}
                />
              ) : null}
            </Stack>,
            (data || dataFromApi) && !isError ? (
              <HighchartsReact
                key="chart-display"
                ref={chartComponent}
                containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
                highcharts={Highcharts}
                options={chartOptions}
              />
            ) : null,
          ]
        )}
      </Box>
    </>
  );
};

export default SMTChart;
